import { Menu, useSidebarState, usePermissions } from 'react-admin';
import { Box, Typography, useMediaQuery } from '@mui/material';
import logo from '../assets/logo.png';
import assistantIcon from '../assets/assistant-icon.png';

import DashboardIcon      from '@mui/icons-material/Dashboard';
import PeopleIcon         from '@mui/icons-material/People';
import ShoppingCartIcon   from '@mui/icons-material/ShoppingCart';
import PaymentIcon        from '@mui/icons-material/Payment';
import InventoryIcon      from '@mui/icons-material/Inventory';
import TuneIcon           from '@mui/icons-material/Tune';
import ContactMailIcon    from '@mui/icons-material/ContactMail';
import LocalShippingIcon  from '@mui/icons-material/LocalShipping';
import EngineeringIcon    from '@mui/icons-material/Engineering';
import ShoppingBagIcon    from '@mui/icons-material/ShoppingBag';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import PriceChangeIcon            from '@mui/icons-material/PriceChange';
import BarChartIcon       from '@mui/icons-material/BarChart';
import RuleIcon           from '@mui/icons-material/Rule';
import InventoryIcon2     from '@mui/icons-material/Inventory2';
import TaskAltIcon        from '@mui/icons-material/TaskAlt';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import HistoryIcon        from '@mui/icons-material/History';
import LanguageIcon       from '@mui/icons-material/Language';
import StorefrontIcon     from '@mui/icons-material/Storefront';
import ReportProblemIcon  from '@mui/icons-material/ReportProblem';
import StarIcon           from '@mui/icons-material/Star';
import GavelIcon          from '@mui/icons-material/Gavel';
import ContactPhoneIcon   from '@mui/icons-material/ContactPhone';
import AccountBoxIcon     from '@mui/icons-material/AccountBox';
import LocalOfferIcon     from '@mui/icons-material/LocalOffer';
import { MOBILE_BREAKPOINT } from '../styles/ds';

const SectionLabel = ({ children }) => (
  <Box sx={{ px: 2.5, pt: 2, pb: 0.5 }}>
    <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: 'rgba(200,169,110,0.55)' }}>
      {children}
    </Typography>
  </Box>
);

const menuSx = {
  display: 'flex', flexDirection: 'column', height: '100%',
  '& .MuiListItemButton-root':   { color: '#ffffff !important' },
  '& .MuiListItemIcon-root':     { color: '#ffffff !important' },
  '& .MuiListItemText-primary':  { color: '#ffffff !important', fontSize: '14px !important' },
  '& .MuiSvgIcon-root':          { color: '#ffffff !important' },
  '& .RaMenuItemLink-active': {
    backgroundColor: 'rgba(200,169,110,0.18) !important',
    borderRight: '3px solid #c8a96e',
    '& .MuiListItemIcon-root':    { color: '#c8a96e !important' },
    '& .MuiListItemText-primary': { color: '#c8a96e !important', fontWeight: '700 !important' },
    '& .MuiSvgIcon-root':         { color: '#c8a96e !important' },
  },
  '& .MuiListItemButton-root:hover': { backgroundColor: 'rgba(200,169,110,0.10) !important' },
  // MUI's default focus-visible overlay is tuned for light backgrounds —
  // against this navy sidebar it's nearly invisible, so keyboard focus
  // needs its own explicit, high-contrast style. react-admin's Menu.Item
  // renders as a MuiMenuItem (not a MuiListItemButton), so this has to
  // target that real class to actually take effect.
  '& .MuiMenuItem-root.Mui-focusVisible': {
    backgroundColor: 'rgba(200,169,110,0.12) !important',
    outline: '2px solid #c8a96e',
    outlineOffset: '-2px',
  },
};

// Wrapper that closes sidebar on mobile when a link is clicked
const NavItem = ({ to, primaryText, leftIcon, resource }) => {
  const [, setSidebarOpen] = useSidebarState();
  const isMobile = useMediaQuery(`(max-width:${MOBILE_BREAKPOINT}px)`);
  return (
    <Menu.Item
      to={to}
      primaryText={primaryText}
      leftIcon={leftIcon}
      resource={resource}
      onClick={() => { if (isMobile) setSidebarOpen(false); }}
    />
  );
};

const DashboardNavItem = () => {
  const [, setSidebarOpen] = useSidebarState();
  const isMobile = useMediaQuery(`(max-width:${MOBILE_BREAKPOINT}px)`);
  return (
    <Menu.DashboardItem
      leftIcon={<DashboardIcon />}
      onClick={() => { if (isMobile) setSidebarOpen(false); }}
    />
  );
};

const CustomMenu = () => {
  const { permissions } = usePermissions();
  const isAdmin = permissions === 'admin';

  return (
  <Menu className="crm-navigation" sx={menuSx}>
    <Box className="crm-navigation-brand" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2.5, px: 2, borderBottom: '1px solid rgba(200,169,110,0.2)', mb: 0.5 }}>
      <img src={logo} alt="פאר הקולמוס" style={{ width: '100%', maxWidth: 165 }} />
    </Box>

    <SectionLabel>ראשי</SectionLabel>
    <DashboardNavItem />
    <NavItem to="/tasks"      primaryText="משימות ומעקבים"  leftIcon={<TaskAltIcon />} />

    <SectionLabel>CRM</SectionLabel>
    <NavItem to="/customers"  primaryText="לקוחות"           leftIcon={<PeopleIcon />}       resource="customers" />
    <NavItem to="/leads"      primaryText="לידים"            leftIcon={<ContactMailIcon />}  resource="leads" />

    <SectionLabel>מכירות</SectionLabel>
    <NavItem to="/orders"     primaryText="הזמנות"           leftIcon={<ShoppingCartIcon />} resource="orders" />
    <NavItem to="/payments"   primaryText="תשלומים"          leftIcon={<PaymentIcon />}      resource="payments" />

    <SectionLabel>קטלוג</SectionLabel>
    <NavItem to="/products"   primaryText="קטגוריות מוצרים" leftIcon={<InventoryIcon />}    resource="products" />
    <NavItem to="/attributes" primaryText="תכונות"           leftIcon={<TuneIcon />}         resource="attributes" />

    <SectionLabel>ייצור וספקים</SectionLabel>
    <NavItem to="/suppliers"          primaryText="ספקים"         leftIcon={<LocalShippingIcon />}          resource="suppliers" />
    <NavItem to="/workers"            primaryText="עובדים"        leftIcon={<EngineeringIcon />}            resource="workers" />
    <NavItem to="/purchases"          primaryText="רכישות"        leftIcon={<ShoppingBagIcon />}            resource="purchases" />
    <NavItem to="/production_batches" primaryText="ייצור ומנות"  leftIcon={<PrecisionManufacturingIcon />} resource="production_batches" />
    <NavItem to="/work-rates"         primaryText="תעריפי עבודה" leftIcon={<PriceChangeIcon />} />

    <SectionLabel>מלאי</SectionLabel>
    <NavItem to="/inventory" primaryText="מצב מלאי" leftIcon={<InventoryIcon2 />} />

    {isAdmin && (
      <>
        <SectionLabel>דוחות</SectionLabel>
        <NavItem to="/profit-report" primaryText="דוח רווחיות" leftIcon={<BarChartIcon />} />
      </>
    )}

    {isAdmin && (
      <>
        <SectionLabel>אתר לקוחות</SectionLabel>
        <NavItem to="/website_products"       primaryText="מוצרי אתר"     leftIcon={<StorefrontIcon />}    resource="website_products" />
        <NavItem to="/website_categories"     primaryText="קטגוריות אתר"  leftIcon={<LanguageIcon />}      resource="website_categories" />
        <NavItem to="/website_best_sellers"   primaryText="Best Sellers"  leftIcon={<StarIcon />} />
        <NavItem to="/website_mapping_issues" primaryText="בעיות מיפוי"   leftIcon={<ReportProblemIcon />} resource="website_mapping_issues" />
        <NavItem to="/website_pages"          primaryText="דפי מדיניות"   leftIcon={<GavelIcon />} resource="website_pages" />
        <NavItem to="/website_accounts"       primaryText="חשבונות אתר"   leftIcon={<AccountBoxIcon />} resource="website_accounts" />
        <NavItem to="/website_shipping"       primaryText="משלוחים"       leftIcon={<LocalShippingIcon />} />
        <NavItem to="/website_coupons"        primaryText="קופונים"       leftIcon={<LocalOfferIcon />} resource="website_coupons" />
        <NavItem to="/website_settings"      primaryText="הגדרות אתר"    leftIcon={<ContactPhoneIcon />} />
      </>
    )}

    <SectionLabel>הגדרות מתקדמות</SectionLabel>
    <NavItem to="/matching_rules" primaryText="כללי התאמה" leftIcon={<RuleIcon />} resource="matching_rules" />
    {isAdmin && (
      <>
        <NavItem to="/users" primaryText="משתמשים"    leftIcon={<ManageAccountsIcon />} resource="users" />
        <NavItem to="/audit" primaryText="יומן פעולות" leftIcon={<HistoryIcon />} />
        <NavItem to="/assistant" primaryText="עוזר חכם"
          leftIcon={<img src={assistantIcon} alt="" style={{ width: 24, height: 24, objectFit: 'contain' }} />} />
      </>
    )}

    <Box sx={{ flexGrow: 1 }} />
    <Box sx={{ borderTop: '1px solid rgba(200,169,110,0.15)', px: 2, py: 1.5, textAlign: 'center' }}>
      <Typography sx={{ fontSize: 10, color: 'rgba(200,169,110,0.4)' }}>פאר הקולמוס CRM v1.0</Typography>
    </Box>
  </Menu>
  );
};

export default CustomMenu;
